import type { ReactNode } from "react";
import { DocumentTitleProvider } from "@/lib/documentTitle/DocumentTitleProvider";
import { WorkspaceTabsProvider } from "@/modules/workspace/WorkspaceTabsProvider";
import { NotificationPrefsProvider } from "@/modules/notifications/NotificationPrefsContext";
import { AppNotificationsLayer } from "@/modules/notifications/AppNotificationsLayer";
import { NotificationPreviewLayer } from "@/modules/notifications/NotificationPreviewLayer";
import { NotificationNavigationListener } from "@/modules/notifications/NotificationNavigationListener";
import { MailComposeProvider } from "@/modules/mail/MailComposeProvider";
import { MessagesQuickAccessProvider } from "@/modules/messages/MessagesQuickAccessProvider";
import { VoiceCallProvider } from "@/modules/voice/VoiceCallProvider";
import { PwaUpdateNotifier } from "@/components/PwaUpdateNotifier";
import { useClientRecordsRealtime } from "@/hooks/useClientRecordsRealtime";

const ClientRecordsRealtime = () => {
  useClientRecordsRealtime();
  return null;
};

export const withLbsMessagesProvider = <P extends { children?: ReactNode }>(
  LayoutComponent: (props: P) => ReactNode,
) => {
  const WrappedLayout = (props: P) => (
    <WorkspaceTabsProvider>
      <DocumentTitleProvider>
      <NotificationPrefsProvider>
        <ClientRecordsRealtime />
        <MailComposeProvider>
          <MessagesQuickAccessProvider>
            <VoiceCallProvider>
              <LayoutComponent {...props} />
            </VoiceCallProvider>
            {/*
          Notification layers need both prefs + messages quick-access.
          Keep them here (not inside MessagesQuickAccessProvider) so the
          provider boundary is explicit and stable across layout remounts.
        */}
            <AppNotificationsLayer />
            <NotificationPreviewLayer />
            <NotificationNavigationListener />
            <PwaUpdateNotifier />
          </MessagesQuickAccessProvider>
        </MailComposeProvider>
      </NotificationPrefsProvider>
      </DocumentTitleProvider>
    </WorkspaceTabsProvider>
  );

  WrappedLayout.displayName = `WithLbsMessagesProvider(${
    (LayoutComponent as { displayName?: string }).displayName ??
    LayoutComponent.name ??
    "Layout"
  })`;

  return WrappedLayout;
};
